import * as angular from "angular"
import { EditLayerCmd } from "../edit/editCmd"
import { convertDatesToJsonUtc, except, FieldViewModel, IDataLayer, IDatalayerEditModel, IDatalayersApiLoader, IDataLayerType, IGenericPlan, IOmniaSession, isArray, isArrayEmpty, ISoilUnitService, LayerTypeId, OmniaModuleId, OmniaObjectType, omniaResolve, OmniaService, setupLayerRelations, SharedDataService, StartLayerEditorArgs } from "../omnia"
import { IDatalayerDeleteResultModel, IDatalayersService, IFieldsService } from "../types"
import { LayerApi } from "./api"
import { HoldingLayers, removeCachedLayers, updateCachedLayer } from "./cache"

import m = omnia.models

export class DatalayersService extends OmniaService implements IDatalayersService {

    private _holdingLayers: HoldingLayers

    private _visibleLayers = new Set<number>()


    static $inject = [
        "$q",
        "OmniaDatalayersApiLoader",
        "OmniaSharedData",
        "OmniaFieldsService",
        "OmniaMapTopologyService",
        "OmniaSoilUnitService",
        "OmniaSession",
        "blockUI"
    ]

    constructor(
        $q: ng.IQService,
        loader: IDatalayersApiLoader,
        public readonly sharedData: SharedDataService,
        private readonly fieldSvc: IFieldsService,
        private readonly topologySvc: omnia.map.IMapTopologyService,
        private readonly soilUnits: ISoilUnitService,
        readonly session: IOmniaSession,
        blockUI: { start(): void, stop(): void },
        private api = new LayerApi($q, loader, session.http, fieldSvc, blockUI, session.debugMode)
    ) {

        /**
         * Note: "soilUnits" is injected to force the initialization of the underlying service
         * at this stage, but is not used directly here
         */

        super(session, {
            name: "Layers",
            module: OmniaModuleId.Layers,
            dependencies: [sharedData, fieldSvc]
        })

    }

    protected getRefreshPromise() {
        this._holdingLayers = null
        return this.api.load(this.session)
    }

    shouldRefresh() {

        const changes = this.apiState.getChangesFromSource(
            this._changeNumber,
            s => (s == OmniaModuleId.Layers)
                || (s == OmniaModuleId.Fields)
                || (s == OmniaModuleId.Seeds)
                || (s == OmniaModuleId.Imports)
                || (s == OmniaModuleId.Soil)
                || (s == OmniaModuleId.FieldPerformance)
                || (s == OmniaModuleId.Satellite)
                || (s == "refresh-all")
                || (!s)
        )

        return !isArrayEmpty(changes)

    }

    getFields(includeDisabled?: boolean) {
        return this.fieldSvc.getFields(includeDisabled)
    }

    getTopologyService() {
        return this.topologySvc
    }

    get selectedField(): FieldViewModel {

        var s = this.fieldSvc.selectedItems

        if (s) {
            s = s.filter(f => f.isField())
            if (s.length) {
                return s[s.length - 1]
            }
        }

    }

    set selectedField(value: FieldViewModel) {
        const items = (value && (value.isField())) ? [value] : []
        this.fieldSvc.selectItems(items, true)
    }


    getLayerTypes(): IDataLayerType[] {
        return this.api.getLayerTypes()
    }

    getCustomLayerType(customTypeId: number, typeId: LayerTypeId): IDataLayerType {
        return this.api.getCustomLayerType(customTypeId, typeId)
    }

    getGenericPlans() {
        return this.api.getGenericPlans(this.yearId)
    }

    getCropYieldScales(year?: number): m.ICropYieldScale[] {
        return this.api.getCropYieldScales(year)
    }

    getCropping(croppingId: number): FieldViewModel {
        return this.fieldSvc.cropping.find(c => c.croppingId === croppingId)
    }

    get fields(): FieldViewModel[] {
        return this.fieldSvc.fields
    }

    get cropping(): FieldViewModel[] {
        return this.fieldSvc.cropping
    }

    get crops() {
        return this.fieldSvc.crops
    }

    get cropVarieties() {
        return this.fieldSvc.cropVarieties
    }

    locateItems(items: FieldViewModel[]) {
        this.fieldSvc.locateItems(items);
    }

    getLayers(ids: number[], includeZones: boolean = false): ng.IPromise<any> {

        return this.getHoldingLayers().then(allLayers => {

            const local = (allLayers || []).filter(l => ids.some(id => l.DataLayerId == id))

            const missing = except(ids, local.map(l => l.DataLayerId))

            const load = isArrayEmpty(missing)
                ? omniaResolve<IDataLayer[]>([])
                : this.api.getLayers(missing)

            return load.then(layers => {

                allLayers = allLayers.concat(layers)

                this._holdingLayers = {
                    roots: allLayers.filter(l => !l.ParentId),
                    all: allLayers
                }

                layers = allLayers.filter(l => ids.some(id => l.DataLayerId == id))

                return includeZones
                    ? this.loadLayerZones(layers)
                    : layers

            })


        })

    }

    setupLayers(layers: IDataLayer[]): IDataLayer[] {
        return this.api.setupLayers(layers)
    }

    getFieldLayers(fieldIds: number[]): ng.IPromise<IDataLayer[]> {

        return this.api.getFieldLayers(fieldIds)

    }

    getHoldingLayers(types?: LayerTypeId[]): ng.IPromise<IDataLayer[]> {

        const p = this._holdingLayers
            ? omniaResolve(this._holdingLayers.roots)
            : this.api.getHoldingLayers(this.holdingId, this.session.yearId).then(layers => {

                this._holdingLayers = {
                    roots: layers.filter(l => !l.ParentId),
                    all: layers
                }

                return this._holdingLayers.roots

            })

        return p.then(layers => isArray(types) && (types.length > 0)
            ? layers.filter(l => types.some(t => t == l.TypeId))
            : layers
        )

    }

    getHoldingYieldLayers(): ng.IPromise<IDataLayer[]> {

        return this.api.getHoldingYieldLayers(this.holdingId)

    }

    getLayerManagerEntries(): ng.IPromise<IDataLayer[]> {

        return this.api.getLayerManagerEntries(this.holdingId).then(layers =>

            layers.filter(l => !l.ParentId)

        )

    }


    loadLayerZones(layers: IDataLayer[], { buffer = false, blockUI = true } = {}): PromiseLike<IDataLayer[]> {

        const ids = layers.map(l => l.DataLayerId)

        const p = buffer
            ? this.api.getLayersBuffered(ids, blockUI)
            : this.api.getLayers(ids)

        return p.then((r: IDataLayer[]) => {

            layers.forEach(l => {
                const ll = r.find(i => i.DataLayerId == l.DataLayerId)
                if (ll) angular.extend(l, ll)
            })

            // Note: @_holdingLayers can be null when used from the fields module
            if (this._holdingLayers) {
                setupLayerRelations(layers, this._holdingLayers.all)
            }

            return layers

        })

    }

    loadLayerPoints(layerId: number): PromiseLike<number[][]> {

        return this.api.loadLayerPoints(layerId)

    }

    getLayerVisibility(layerId: number) {
        return this._visibleLayers.has(layerId)
    }

    setLayerVisibility(layerId: number, visible: boolean) {

        if (visible) {
            this._visibleLayers.add(layerId)
        }
        else {
            this._visibleLayers.delete(layerId)
        }

    }

    getVisibleLayers() {
        return Array.from(this._visibleLayers.keys())
    }

    setVisibleLayers(ids: number[]) {
        this._visibleLayers = new Set(ids)
    }

    editLayer(data: IDatalayerEditModel): ng.IPromise<IDataLayer> {

        data = { ...data, Parent: undefined, Children: undefined }

        const url = data.DataLayerId
            ? "/api/datalayers/layer/update"
            : "/api/datalayers/layer/create"

        convertDatesToJsonUtc(data)

        const post = this.post(url, data, undefined, false)

        return post.then(r => {

            const result: {
                Layer: IDataLayer,
                UpdatedPlans: IGenericPlan[],
                DeletedPlans: number[]
            } = r

            const layer = result.Layer

            const plans = {

                UpdatedPlans: layer.GenericPlan
                    ? (result.UpdatedPlans || []).concat(layer.GenericPlan)
                    : result.UpdatedPlans,

                DeletedPlans: result.DeletedPlans

            }

            this.api.refreshGenericPlans(plans)

            this._changeNumber = this.session.getApiVersion()
            this.api.setupLayers([layer])

            updateCachedLayer(this._holdingLayers, layer, this.api.getGenericPlans())

            return layer

        })

    }

    canDeleteLayer(layerId: number): ng.IPromise<IDatalayerDeleteResultModel> {

        const url = "/api/datalayers/layer/delete/check/" + layerId

        return this.ohttp.get(url).then(r => r.data)

    }

    deleteLayers(layerIds: number[], deleteAnalysis: boolean): ng.IPromise<any> {

        const url = "/api/layers/delete?layerIds=" + layerIds.join(",") + (deleteAnalysis ? "&deleteAnalysis=true" : "")

        const del = this.delete(url, undefined, undefined, false)

        return del.then(data => {

            this.api.refreshGenericPlans(data)

            this._changeNumber = this.session.getApiVersion()
            removeCachedLayers(this._holdingLayers, layerIds, this.api.getGenericPlans())

        })

    }

    enableOrDisableLayers(layers: number[], disable: boolean): ng.IPromise<any> {

        let url = disable
            ? "/api/layers/disable"
            : "/api/layers/enable"

        url = `${url}?layerIds=${layers.join(",")}`

        return this.post(url)

    }

    saveYieldScales(scales: m.ICropYieldScale[], removedScalesIds: number[]): ng.IPromise<any> {

        const url = "/api/datalayers/yieldscales/save"

        const data = { scales, removedScalesIds }

        return this.post(url, data, { type: OmniaObjectType.YieldMapScale })

    }

    startLayerEditor(args: StartLayerEditorArgs, context: omnia.shell.IComponentLauncher): ng.IPromise<any> {

        const cmd = new EditLayerCmd(context)

        const {
            layer,
            field,
            config: options
        } = args

        const r = cmd.editLayer({
            layer,
            target: field,
            config: options
        })

        return r.result

    }

}
